import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import {
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "@amzn/stencil-react-components/modal";
import { Radio } from "@amzn/stencil-react-components/radio";
import { Label, Text } from "@amzn/stencil-react-components/text";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { emitPermissionsReportOpened } from "@/helpers/mac-permissions-metrics";
import { type ReportCause, REPORT_CAUSES } from "@/types/agentcore";

import { reportCauseMessageId } from "./constants";

/** Modal's `aria-labelledby` points here, which is the only way its role=dialog gets a name. */
export const REPORT_HEADING_ID = "macOsPermissionsReportHeading";

/** Names the radio group, so the question is announced with the options rather than before them. */
const REPORT_QUESTION_ID = "macOsPermissionsReportQuestion";

interface ReportIssueModalProps {
  /** Dismissed without sending anything. */
  readonly onClose: () => void;
  /**
   * A cause was chosen. The caller records it and decides the outcome, because by now the grants
   * may have landed and a refusal must not be reported for a hire who is no longer refusing.
   */
  readonly onSubmitted: (cause: ReportCause) => void;
}

/**
 * Asks why a grant could not be turned on.
 *
 * Submitting records a cause as telemetry, not a support ticket. Whether it ends the walkthrough is
 * the caller's decision, because the grants can land while this dialog is open.
 */
export function ReportIssueModal({
  onClose,
  onSubmitted,
}: ReportIssueModalProps): JSX.Element {
  const intl = useIntl();
  const [cause, setCause] = useState<ReportCause | undefined>(undefined);

  /* Opening is the event, so it is counted on mount and not on submit -- abandoning still counts. */
  useEffect(() => {
    emitPermissionsReportOpened();
  }, []);

  const submit = (): void => {
    /* istanbul ignore next: Submit is disabled until a cause is chosen, so this only narrows the type. */
    if (!cause) {
      return;
    }
    onSubmitted(cause);
  };

  return (
    <Col gridGap="S300" dataTestId="macOsPermissionsReportModal">
      <ModalHeader
        onCloseButtonClick={onClose}
        closeButtonAltText={intl.formatMessage({
          id: "macPermissions-report-close-label",
        })}
      >
        <Text fontWeight="bold" id={REPORT_HEADING_ID}>
          <FormattedMessage id="macPermissions-report-issue-link" />
        </Text>
      </ModalHeader>

      <ModalBody>
        <Col gridGap="S300">
          <Text fontWeight="regular" id={REPORT_QUESTION_ID}>
            <FormattedMessage id="macPermissions-report-issue-heading" />
          </Text>

          {/* The question is the group's name: without it a screen reader reaches four bare
              options and never hears what is being asked. */}
          <Col
            gridGap="S300"
            role="radiogroup"
            aria-labelledby={REPORT_QUESTION_ID}
          >
            {REPORT_CAUSES.map((option) => (
              <Row key={option} alignItems="center" gridGap="S200">
                <Radio
                  id={`macOsPermissionsCause-${option}`}
                  name="macOsPermissionsCause"
                  value={option}
                  checked={cause === option}
                  onChange={() => setCause(option)}
                />
                <Label
                  htmlFor={`macOsPermissionsCause-${option}`}
                  color="color.text.primary"
                >
                  <FormattedMessage id={reportCauseMessageId(option)} />
                </Label>
              </Row>
            ))}
          </Col>
        </Col>
      </ModalBody>

      <ModalFooter>
        <Row gridGap="S400">
          <Button
            variant={ButtonVariant.Secondary}
            onClick={onClose}
            dataTestId="macOsPermissionsReportCancelButton"
          >
            <FormattedMessage id="macPermissions-report-cancel-button" />
          </Button>
          <Button
            variant={ButtonVariant.Primary}
            onClick={submit}
            disabled={cause === undefined}
            dataTestId="macOsPermissionsReportSubmitButton"
          >
            <FormattedMessage id="macPermissions-report-submit-button" />
          </Button>
        </Row>
      </ModalFooter>
    </Col>
  );
}
